import React, { useState, useRef, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react'; 
import { motion, AnimatePresence } from 'framer-motion'; // Using framer-motion from your architecture
import { useKernel } from '../hooks/AIKernel'; // The Symbolic Engine
import './LinkForm.css';

const LinkForm = ({ populateLinkList }) => {
  // --- AI Kernel Integration ---
  const { agentState, reportBehavior } = useKernel();
  // --- Component State ---
  const [doesPlaceholderAppear, setDoesPlaceholderAppear] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [shortenedUrl, setShortenedUrl] = useState('');
  const [showQRCode, setShowQRCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const qrRef = useRef(null);
  const linkToShortenRef = useRef(null);

  // URL Validation function
  const isValidUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:')
        && Boolean(parsedUrl.hostname)
        && !parsedUrl.username
        && !parsedUrl.password;
    } catch {
      return false;
    }
  };

  const shortenLink = async (event) => {
    event.preventDefault();

    const url = inputValue.trim();
    if (!isValidUrl(url)) {
      setErrorMessage('Invalid URL! Please enter a valid format like "https://www.google.com".');
      
      // 🧠 AI Sensor: Report friction/error state to the matrix
      reportBehavior('form_friction', 1, 'behavior');
      return;
    }

    setErrorMessage(''); 
    setIsSubmitting(true);

    if (!doesPlaceholderAppear) {
      let didShorten = false;
      try {
        const response = await fetch('https://apiv1.harishacrypto.me/service6/api/shorten', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-api-key':'super-secret-admin-key' // Note: Removed the typo space before super-secret
          },
          body: JSON.stringify({ url }) 
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.shortUrl) {
          throw new Error('Invalid response structure: missing "result_url"');
        }

        let linkData = {
          shortcode: data.shortUrl,
          visits: 1,
          lastVisited: Date.now(),
          originalLink: url
        };

        populateLinkList(linkData);
        setShortenedUrl(data.shortUrl);
        setShowQRCode(true); 
        didShorten = true;

        // 🧠 AI Sensor: Report successful interaction & utility interest
        reportBehavior('productivity_utility', 5, 'interest');
        reportBehavior('successful_conversions', 1, 'behavior');

      } catch (error) {
        console.error('Error fetching shortened URL:', error);
        setErrorMessage('We could not shorten that link. Please try again in a moment.');
        // 🧠 AI Sensor: Report API failure friction
        reportBehavior('api_failure_friction', 2, 'behavior');
      } finally {
        setIsSubmitting(false);
      }

      if (didShorten) {
        setInputValue('');
        setDoesPlaceholderAppear(true);
      }
    }
  };

  const onTextInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    setDoesPlaceholderAppear(value.length === 0);
    setErrorMessage(''); 
  };

  const downloadQRCode = () => {
    const canvas = qrRef.current.querySelector('canvas');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = 'qrcode.png';
    link.click();
    
    // 🧠 AI Sensor: Highly engaged user behavior
    reportBehavior('qr_downloads', 3, 'interest');
  };

  // --- Adaptive UI Logic ---
  const buttonBaseClass = doesPlaceholderAppear ? 'shorten-button-disabled' : 'shorten-button';
  // If the AI detects frustration or high interest, it requests the UI to highlight CTAs
  const highlightClass = agentState.ui.highlightCTAs && !doesPlaceholderAppear ? 'cta-highlight-glow' : '';

  return (
    <div className={`url-form ${agentState.theme}`}>
      <form onSubmit={shortenLink}>
        <input
          className="url-input normal-text"
          ref={linkToShortenRef}
          type="url"
          inputMode="url"
          autoComplete="url"
          aria-label="Destination URL"
          aria-invalid={Boolean(errorMessage)}
          value={inputValue}
          onChange={onTextInputChange}
          placeholder="https://example.com/your-long-link"
          disabled={isSubmitting}
        />
        
        {/* Adaptive Error Message */}
        <AnimatePresence>
            {errorMessage && (
                <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="error-text"
                >
                    {errorMessage}
                </motion.p>
            )}
        </AnimatePresence>

        {/* Adaptive CTA Button */}
        <motion.button
          className={`${buttonBaseClass} normal-text ${highlightClass}`}
          type="submit"
          disabled={doesPlaceholderAppear || isSubmitting}
          // AI Actuator: Turn off hover animations if user is frustrated (simplifyAnimations: true)
          whileHover={!agentState.ui.simplifyAnimations && !doesPlaceholderAppear ? { scale: 1.05 } : {}}
          whileTap={!agentState.ui.simplifyAnimations && !doesPlaceholderAppear ? { scale: 0.95 } : {}}
        >
          {isSubmitting ? 'Creating link…' : 'Shorten link'}
        </motion.button>
      </form>

      {/* QR Code Popup */}
      <AnimatePresence>
        {showQRCode && (
          <motion.div 
            className="qr-popup"
            initial={agentState.ui.simplifyAnimations ? { opacity: 1 } : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={agentState.ui.simplifyAnimations ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
          >
            <div className="qr-content" role="dialog" aria-modal="true" aria-labelledby="qr-dialog-title">
              <h3 id="qr-dialog-title">Your link is ready</h3>
              <p>Scan the QR code to open your shortened link.</p>
              <div ref={qrRef} className="qr-code-frame">
                <QRCodeCanvas value={shortenedUrl} size={200} />
              </div>
              <div className="qr-actions">
                  <button className="download-btn" type="button" onClick={downloadQRCode}>Download QR</button>
                  <button className="close-btn" type="button" onClick={() => setShowQRCode(false)}>Done</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LinkForm;